import React, { useState, useEffect, useRef } from "react";
import pausebuttonlogo from "../../assets/pause.svg";
import playbuttonlogo from "../../assets/play.svg";
import rewindBackIcon from "../../assets/RewindBack.svg";
import rewindForwardIcon from "../../assets/RewindForward.svg";
import "./VideoControls.css";
import volumeIcon from "../../assets/volume.svg";
import volumeActiveIcon from "../../assets/volumeActive.svg";
import volumeOffIcon from "../../assets/volumeOff.svg";
import volumeOffActiveIcon from "../../assets/volumeOffActive.svg";
import qualityIcon from "../../assets/quality.svg";
import speedIcon from "../../assets/speed.svg";
import ccIcon from "../../assets/cc.svg";
import wideIcon from "../../assets/wide.svg";
import fullScreenIcon from "../../assets/fullscreen.svg";
import arrowDown from "../../assets/arrowDown.svg";
import SpeedMenu from "./SpeedMenu";
import clockIcon from "../../assets/clock.svg";
import starIconPurple from "../../assets/starIconPurple.svg";
import { ReactComponent as StarIcon } from "../../assets/starIconPurple.svg";
import StarIconComponent from "../StarIconComponent/StarIconComponent";
import DurationMenu from "./DurationMenu";
import { isTypeNode } from "typescript";
const formatTime = (seconds) => {
  if (!seconds) return "00:00";

  const totalMinutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  const pad = (num) => String(num).padStart(2, "0");

  return `${pad(totalMinutes)}:${pad(remainingSeconds)}`;
};

const lerp = (start, end, factor) => {
  return start + (end - start) * factor;
};

const VideoControls = ({
  currentTime = 0,
  totalTime = 0,
  progress = 0,
  speed = 1,
  setSpeed = () => {},
  setNewMarkerInfo = () => {},
  newMarkerInfo,
  videoControls,
  isWideMode,
  onWideModeChange,
  isSharePopupOpen = false,
  isUsersPopupOpen = false,
  comments = [],
  onCommentMarkerClick, // NEW PROP
}) => {
  const [isVolumeHovered, setIsVolumeHovered] = useState(false);
  const [volume, setVolume] = useState(1);
  const [showRewindMenu, setShowRewindMenu] = useState(false);
  const [showForwardMenu, setShowForwardMenu] = useState(false);
  const [rewindDuration, setRewindDuration] = useState(5);
  const [forwardDuration, setForwardDuration] = useState(5);
  const [showSpeedMenu, setShowSpeedMenu] = useState(false);
  const speedButtonRef = useRef(null);
  const rewindButtonRef = useRef(null);
  const forwardButtonRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    if (videoControls) {
      setVolume(videoControls.getVolume());
    }
  }, [videoControls]);

  useEffect(() => {
    const onSeekNearby = (e) => {
      const isInputFocused =
        !!document.querySelector("textarea.comment-input:focus") ||
        !!document.querySelector(".reply-input:focus");
      if (
        e.code === "ArrowLeft" &&
        !isSharePopupOpen &&
        !isUsersPopupOpen &&
        !isInputFocused
      ) {
        e.preventDefault();
        const newTime = Math.max(0, currentTime - rewindDuration);
        videoControls.seek(newTime);
        if (!videoControls.isPlaying) {
          const basePosition = Math.min((newTime / totalTime) * 100, 100);
          setNewMarkerInfo({
            ...newMarkerInfo,
            timecode: newTime,
            position: basePosition,
            positionEnd: null,
            timecodeEnd: null,
          });
        }
      } else if (
        e.code === "ArrowRight" &&
        !isSharePopupOpen &&
        !isUsersPopupOpen &&
        !isInputFocused
      ) {
        e.preventDefault();
        const newTime = Math.max(0, currentTime + rewindDuration);
        videoControls.seek(newTime);
        if (!videoControls.isPlaying) {
          const basePosition = Math.min((newTime / totalTime) * 100, 100);
          setNewMarkerInfo({
            ...newMarkerInfo,
            timecode: newTime,
            position: basePosition,
            positionEnd: null,
            timecodeEnd: null,
          });
        }
      }
    };
    window.addEventListener("keydown", onSeekNearby);
    return () => window.removeEventListener("keydown", onSeekNearby);
  }, [
    videoControls,
    currentTime,
    rewindDuration,
    isSharePopupOpen,
    isUsersPopupOpen,
  ]);

  useEffect(() => {
    if (!videoControls) return;
    videoControls.setPlaybackRate(speed);
  }, [speed]);

  const handlePlayPause = React.useCallback(() => {
    if (!videoControls) return;

    console.log(123123);

    if (videoControls.isPlaying) {
      setIsPlaying(false);
      videoControls.pause();
    } else {
      setIsPlaying(true);
      videoControls.play();
    }
  }, [videoControls]);

  React.useEffect(() => {
    const handleSpace = (e) => {
      const target = e.target;
      const isEditable =
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.isContentEditable;
      if (isEditable) return;
      if (e.code === "Space" || e.key === " " || e.keyCode === 32) {
        e.preventDefault();
        handlePlayPause();
      }
    };
    window.addEventListener("keydown", handleSpace);
    return () => window.removeEventListener("keydown", handleSpace);
  }, [handlePlayPause]);
  const handleFullscreen = () => {
    if (!videoControls) return;
    videoControls.toggleFullscreen();
  };

  const handleWide = () => {
    if (!videoControls) return;
    const newWideMode = videoControls.toggleWide();
    onWideModeChange?.(newWideMode);
  };

  const handleVolumeChange = (e) => {
    if (!videoControls) return;
    e.preventDefault();
    e.stopPropagation();
    const newVolume = parseFloat(e.target.value);
    videoControls.setVolume(newVolume);
    setVolume(newVolume);
    e.target.style.setProperty("--volume-percentage", `${newVolume * 100}%`);
  };

  const handleRewind = () => {
    const newTime = Math.max(0, currentTime - rewindDuration);
    videoControls.seek(newTime);
    if (!videoControls.isPlaying) {
      const basePosition = Math.min((newTime / totalTime) * 100, 100);
      setNewMarkerInfo({
        ...newMarkerInfo,
        timecode: newTime,
        position: basePosition,
        positionEnd: null,
        timecodeEnd: null,
      });
    }
  };

  const handleForward = () => {
    const newTime = Math.min(totalTime, currentTime + forwardDuration);
    videoControls.seek(newTime);
    if (!videoControls.isPlaying) {
      const basePosition = Math.min((newTime / totalTime) * 100, 100);
      setNewMarkerInfo({
        ...newMarkerInfo,
        timecode: newTime,
        position: basePosition,
        positionEnd: null,
        timecodeEnd: null,
      });
    }
  };

  const handleRewindArrowClick = (e) => {
    e.stopPropagation();
    setShowRewindMenu((prev) => !prev);
    setShowForwardMenu(false);
  };

  const handleForwardArrowClick = (e) => {
    e.stopPropagation();
    setShowForwardMenu((prev) => !prev);
    setShowRewindMenu(false);
  };

  const handleSpeedChange = (speedValue) => {
    if (!videoControls) return;
    videoControls.setPlaybackRate(speedValue);
    setSpeed(speed);
    setShowSpeedMenu(false);
  };

  const handleDurationChange = (duration) => {
    if (showRewindMenu) {
      setRewindDuration(duration);
    } else if (showForwardMenu) {
      setForwardDuration(duration);
    }

    setShowRewindMenu(false);
    setShowForwardMenu(false);
  };

  const getSpeedMenuPosition = () => {
    if (!speedButtonRef.current) return {};
    const rect = speedButtonRef.current.getBoundingClientRect();
    return {
      bottom: window.innerHeight - rect.top + 8,
      left: rect.left,
    };
  };

  const getDurationMenuPosition = () => {
    if (!showForwardMenu && !showRewindMenu) return {};

    if (showRewindMenu) {
      if (!rewindButtonRef.current) return {};
      const rect = rewindButtonRef.current.getBoundingClientRect();
      return {
        bottom: window.innerHeight - rect.top + 8,
        left: rect.left - 32,
      };
    }

    if (showForwardMenu) {
      if (!forwardButtonRef.current) return {};
      const rect = forwardButtonRef.current.getBoundingClientRect();
      return {
        bottom: window.innerHeight - rect.top + 8,
        left: rect.left - 4,
      };
    }

    return {};
  };

  const generateTimelineElements = () => {
    let elements = [];

    const FourDots = () => (
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          width: "3.5%",
        }}
      >
        <div className="timeline-dot" />
        <div className="timeline-dot" />
        <div className="timeline-dot" />
        <div className="timeline-dot" />
      </div>
    );

    const Timestamp = ({ time }) => (
      <div className="timeline-marker">
        <span className="marker-time">{time}</span>
      </div>
    );

    for (let i = 0; i < 12; i++) {
      let time = "00:00";
      if (i === 0) {
        time = "00:00";
      } else if (i == 11) {
        time = formatTime(totalTime);
      } else {
        time = formatTime((i / 11) * totalTime);
      }
      elements.push(() => <Timestamp time={time} />);
      elements.push(() => <FourDots />);
    }

    elements = elements.slice(0, -1);
    return elements;
  };

  const generateCommentMarkers = () => {
    const result = comments.map((comment) => {
      // if (comment.timecode.includes(" - ")) {
      //   const [timecodeStart] = comment.timecode.split(" - ");
      //   const timeInSeconds = +timecodeStart;
      //   const basePosition = Math.min((timeInSeconds / totalTime) * 100, 100);
      //   position = 1.5 + (basePosition * 96.9) / 100;
      // } else {
      //   const timeInSeconds = +comment.timecode;
      //   const basePosition = Math.min((timeInSeconds / totalTime) * 100, 100);
      //   position = 1.5 + (basePosition * 96.9) / 100;
      // }

      const timeInSeconds = +comment.timecode;
      const basePosition = Math.min((timeInSeconds / totalTime) * 100, 100);

      return {
        id: comment.id,
        position: basePosition,
        timecode: comment.timecode,
        color: comment.tags.length ? comment.tags[0].color : null,
      };
    });

    return result;
  };

  const handleSeekTimecode = (timecode) => {
    if (timecode.includes("-")) {
      const [timecodeStart, timecodeEnd] = timecode.split(" - ");
      videoControls.seek(+timecodeStart);
    } else {
      videoControls.seek(timecode);
    }
  };

  return (
    <div className="video-controls">
      <div className="playback-controls">
        <button
          className="control-btn speed-button"
          ref={speedButtonRef}
          onClick={() => setShowSpeedMenu(!showSpeedMenu)}
        >
          {/* <img src={speedIcon} alt="speed" width="32" height="32" /> */}
          <span className="speed-label">x{speed}</span>
        </button>
        <div
          className="volume-control"
          onMouseEnter={() => setIsVolumeHovered(true)}
          onMouseLeave={(e) => {
            e.stopPropagation();
            if (!e.currentTarget) return;
            const sliderContainer = e.currentTarget.querySelector(
              ".volume-slider-container"
            );
            if (sliderContainer && !sliderContainer.contains(e.relatedTarget)) {
              setIsVolumeHovered(false);
            }
          }}
          onClick={(e) => {
            if (e.target.tagName !== "IMG") return;
            if (volume === 0) {
              videoControls.setVolume(0.5);
              setVolume(0.5);
            } else {
              videoControls.setVolume(0);
              setVolume(0);
            }
          }}
        >
          <button className="control-btn">
            <img
              src={
                volume === 0
                  ? isVolumeHovered
                    ? volumeOffActiveIcon
                    : volumeOffIcon
                  : isVolumeHovered
                    ? volumeActiveIcon
                    : volumeIcon
              }
              alt="volume"
              width="32"
              height="32"
            />
          </button>
          {isVolumeHovered && (
            <div
              onMouseLeave={(e) => {
                if (
                  e.currentTarget &&
                  !e.currentTarget.parentElement.contains(e.relatedTarget)
                ) {
                  setIsVolumeHovered(false);
                }
              }}
              className="volume-slider-container"
            >
              <div className="volume-slider-content">
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  value={volume}
                  onChange={handleVolumeChange}
                  className="volume-slider"
                  style={{ "--volume-percentage": `${volume * 100}%` }}
                />
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="center-tools">
        <button ref={rewindButtonRef} className="control-btn rewind">
          <img
            src={rewindBackIcon}
            alt="rewind back"
            width="40"
            height="40"
            className="rewind-circle"
            onClick={handleRewind}
          />
          <img
            src={arrowDown}
            alt="show menu"
            width="16"
            height="16"
            className="menu-arrow left"
            onClick={handleRewindArrowClick}
          />
          <span className="skip-label-left">{rewindDuration}</span>
        </button>
        <button className="control-btn play-pause" onClick={handlePlayPause}>
          <img
            src={videoControls?.isPlaying ? pausebuttonlogo : playbuttonlogo}
            alt={videoControls?.isPlaying ? "pause" : "play"}
            width="40"
            height="40"
          />
        </button>
        <button ref={forwardButtonRef} className="control-btn forward">
          <img
            src={rewindForwardIcon}
            alt="rewind forward"
            width="40"
            height="40"
            className="rewind-circle"
            onClick={handleForward}
          />
          <img
            src={arrowDown}
            alt="show menu"
            width="16"
            height="16"
            className="menu-arrow right"
            onClick={handleForwardArrowClick}
          />
          <span className="skip-label">{forwardDuration}</span>
        </button>
      </div>
      <div className="right-controls">
        <button className="control-btn" onClick={handleWide}>
          <img src={wideIcon} alt="wide" width="32" height="32" />
        </button>
        <button className="control-btn" onClick={handleFullscreen}>
          <img src={fullScreenIcon} alt="fullscreen" width="32" height="32" />
        </button>
      </div>

      <SpeedMenu
        isOpen={showSpeedMenu}
        onClose={() => setShowSpeedMenu(false)}
        currentSpeed={speed}
        onSpeedChange={handleSpeedChange}
        position={getSpeedMenuPosition()}
      />
      <DurationMenu
        showForwardMenu={showForwardMenu}
        showRewindMenu={showRewindMenu}
        onClose={() => {
          if (showForwardMenu) {
            setShowForwardMenu(false);
          } else if (showRewindMenu) {
            setShowRewindMenu(false);
          }
        }}
        rewindDuration={rewindDuration}
        forwardDuration={forwardDuration}
        onDurationChange={handleDurationChange}
        position={getDurationMenuPosition()}
      />
    </div>
  );
};

export default VideoControls;
